import { useMemo } from "react";
import {
  useCheckoutState,
  useCheckoutStateSelector,
} from "@checkout/context/hooks";
import { getParentState } from "@checkout/helpers";
import { ParentState } from "@checkout/types/common";

export const useParentState = () => {
  const [state] = useCheckoutState();

  const currentState = getParentState(state?.value);

  const isInLoadingStep = ParentState[currentState] === ParentState.loading;

  const isInQuoteStep = ParentState[currentState] === ParentState.cartQuote;

  const isInUpdateStep = ParentState[currentState] === ParentState.cartUpdate;

  const isInFulfillStep = ParentState[currentState] === ParentState.cartFulfill;

  const isInSeatSelectionStep =
    ParentState[currentState] === ParentState.seatSelection;

  const isInTripInsuranceStep =
    ParentState[currentState] === ParentState.insurance;

  const isInCardPaymentStep =
    ParentState[currentState] === ParentState.cardPayment;

  const isInReviewStep = ParentState[currentState] === ParentState.review;

  const isInBookingConfirmationStep =
    ParentState[currentState] === ParentState.bookingConfirmation;

  const hasCompletedPassengerInformationStep =
    ParentState[currentState] !== ParentState.contactAndTravelerInformation;

  const hasAdditionalDetailsStep = useCheckoutStateSelector(
    (s) => s.context[ParentState.activityInformation]?.hasAdditionalDetailsStep
  );

  const statesAfterActivityInformation = [
    ParentState.cartQuote,
    ParentState.cartUpdate,
    ParentState.cardPayment,
    ParentState.awardsPayment,
    ParentState.review,
    ParentState.cartFulfill,
    ParentState.bookingConfirmation,
  ];

  const hasCompletedActivityStep = hasAdditionalDetailsStep
    ? statesAfterActivityInformation.includes(ParentState[currentState])
    : true;

  const hasCompletedRequiredSteps = hasAdditionalDetailsStep
    ? hasCompletedActivityStep
    : hasCompletedPassengerInformationStep;

  const hasCompletedSeatSelectionStep =
    ParentState[currentState] !== ParentState.seatSelection &&
    !isInQuoteStep &&
    hasCompletedPassengerInformationStep;

  const hasCompletedTripInsuranceStep =
    !isInTripInsuranceStep && hasCompletedSeatSelectionStep;

  const isPaymentDisabled =
    !hasCompletedRequiredSteps ||
    isInQuoteStep ||
    !hasCompletedTripInsuranceStep;

  const participantCount = useCheckoutStateSelector(
    (state) =>
      state.context[ParentState.activityInformation]?.participantCount ?? 0
  );
  const travelerBookingQuestions = useCheckoutStateSelector(
    (state) =>
      state.context[ParentState.activityInformation]?.travelerBookingQuestions
  );
  const hasPrimaryParticipantSelectionStep =
    participantCount > 1 && travelerBookingQuestions?.length > 0;

  // Primary participant step is completed when we're past it (at activityInformation or later)
  const statesAfterPrimaryParticipant = [
    "activityInformation",
    ...statesAfterActivityInformation,
  ];
  const hasCompletedPrimaryParticipantStep = hasPrimaryParticipantSelectionStep
    ? statesAfterPrimaryParticipant.includes(ParentState[currentState])
    : true;

  return useMemo(
    () => ({
      parentStateContext: {
        currentState,
        isInQuoteStep,
        isInUpdateStep,
        isInFulfillStep,
        isInSeatSelectionStep,
        isInTripInsuranceStep,
        isInCardPaymentStep,
        isInReviewStep,
        isInBookingConfirmationStep,
        hasCompletedPassengerInformationStep,
        hasCompletedPrimaryParticipantStep,
        hasCompletedActivityStep,
        hasAdditionalDetailsStep,
        isPaymentDisabled,
        hasPrimaryParticipantSelectionStep,
        hasCompletedTripInsuranceStep,
        hasCompletedSeatSelectionStep,
        isInLoadingStep,
      },
    }),
    [
      currentState,
      hasCompletedPassengerInformationStep,
      hasCompletedPrimaryParticipantStep,
      isInCardPaymentStep,
      isInFulfillStep,
      isInQuoteStep,
      isInReviewStep,
      isInSeatSelectionStep,
      isInTripInsuranceStep,
      isInUpdateStep,
      hasCompletedActivityStep,
      hasAdditionalDetailsStep,
      isPaymentDisabled,
      hasPrimaryParticipantSelectionStep,
      hasCompletedTripInsuranceStep,
      hasCompletedSeatSelectionStep,
      isInBookingConfirmationStep,
      isInLoadingStep,
    ]
  );
};
